import React, { useMemo, useState } from 'react'

import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Icon,
  IconButton,
  IconProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemProps,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Portal,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
  UseDisclosureReturn,
} from '@chakra-ui/react'

import { Interview, SummaryLanguage, SummaryPart } from '@pkg/types/interview'

import { isAllowlistedForInterviewPdfExport } from '@/helpers/is-allowlisted'
import {
  useDeleteSummaryPart,
  useGenerateSummaryPart,
  useInterview,
  useRegenerateSummary,
  useUpdateInterview,
  useUpdateSummaryParts,
} from '@/react-query/interview/interview'
import { useUser } from '@/react-query/user'
import { ArrowRightSvg } from '@/views/assets/ArrowRightSvg'
import { CustomizeIcon } from '@/views/assets/CustomizeIcon'
import { DeleteIcon } from '@/views/assets/DeleteIcon'
import { LanguageSvg } from '@/views/assets/LanguageSvg'
import { PlusIcon } from '@/views/assets/PlusIcon'
import { RefreshIcon } from '@/views/assets/RefreshIcon'
import Card from '@/views/components/Card'
import { CheckboxIcon } from '@/views/components/CheckboxIcon/CheckboxIcon'
import { CopyContentButton } from '@/views/components/CopyContentButton/CopyContentButton'
import { ExportPdfButton } from '@/views/screens/Interview/components/ExportPdfButton'

import { formatScorecardPartForCopy } from './formatScorecardPartTitle'
import { ViewATSScorecardButton } from './ViewATSScorecardButton'

const DEFAULT_PART_NAMES = {
  experience: 'Experience',
  highlights: 'Highlights',
  concerns: 'Concerns',
  facts: 'Facts',
  skills: 'Skills',
  interviewerFeedback: 'Interviewer Feedback',
}

function isAllowlistedForInterviewerFeedback(
  userEmail: string | undefined,
): boolean {
  if (!userEmail) return false
  return (
    userEmail.endsWith('@hirehire.me') ||
    userEmail.includes('verycrazyslava@gmail.com') ||
    userEmail.includes('pansslava@gmail.com')
  )
}

type AvailablePart = {
  key: string
  name: string
  customSummaryPartPromptId?: string
  generatedPart?: SummaryPart
  isGenerated: boolean
}

function getAvailableSummaryParts(
  interview: Interview | null | undefined,
  userEmail: string | undefined,
): AvailablePart[] {
  if (!interview) return []

  const parts: AvailablePart[] = []
  const existingParts = interview.SummaryParts || []

  Object.values(DEFAULT_PART_NAMES).forEach((partName) => {
    // Skip Interviewer Feedback if user is not allowlisted
    if (
      partName === 'Interviewer Feedback' &&
      !isAllowlistedForInterviewerFeedback(userEmail)
    ) {
      return
    }

    const existingPart = existingParts.find((p) => p.name === partName)
    parts.push({
      key: partName,
      name: partName,
      generatedPart: existingPart,
      isGenerated: !!existingPart,
    })
  })

  const customPrompts = interview.Role?.CustomSummaryPartPrompt || []
  customPrompts.forEach((prompt) => {
    const existingPart = existingParts.find(
      (p) => p.customSummaryPartPromptId === prompt.id,
    )
    parts.push({
      key: prompt.id,
      name: prompt.name,
      customSummaryPartPromptId: prompt.id,
      generatedPart: existingPart,
      isGenerated: !!existingPart,
    })
  })

  return parts.sort((a, b) => {
    const orderA = a.generatedPart?.order ?? Number.MAX_SAFE_INTEGER
    const orderB = b.generatedPart?.order ?? Number.MAX_SAFE_INTEGER
    return orderA - orderB
  })
}

type ToolboxProps = {
  isCopyDisabled?: boolean
  copyRef?: React.RefObject<HTMLDivElement | null>
  isAddCustomSummaryPartShown: boolean
  setIsAddCustomSummaryPartShown: React.Dispatch<React.SetStateAction<boolean>>
  isCandidateSummaryPartsRegenerating?: boolean
  customizeDisclosure?: UseDisclosureReturn
}
export const Toolbox = ({
  isCopyDisabled,
  copyRef,
  customizeDisclosure,
  setIsAddCustomSummaryPartShown,
}: ToolboxProps) => {
  const interview = useInterview().data
  const summaryLanguage = interview?.summaryLanguage
  const summaryParts = interview?.SummaryParts
  const user = useUser().data

  const updateInterview = useUpdateInterview()
  const regenerateSummary = useRegenerateSummary()
  const generateSummaryPart = useGenerateSummaryPart()
  const languageSubmenuDisclosure = useDisclosure()
  const updateSummaryParts = useUpdateSummaryParts()

  const [generatingPartKeys, setGeneratingPartKeys] = useState<Set<string>>(
    new Set(),
  )

  const interviewType = interview?.type

  const availableParts = useMemo(
    () => getAvailableSummaryParts(interview, user?.email),
    [
      interview?.SummaryParts?.map((p) => `${p.id}:${p.order}:${p.isSelected}`)
        .sort()
        .join(','),
      interview?.Role?.CustomSummaryPartPrompt?.map((p) => p.id).join(','),
      user?.email,
    ],
  )

  let closeLanguageSubmenuTimeoutId: NodeJS.Timeout

  const handleLanguageSubmenuOpen = () => {
    clearTimeout(closeLanguageSubmenuTimeoutId)
    languageSubmenuDisclosure.onOpen()
  }

  const handleLanguageSubmenuClose = () => {
    // Set a delay before closing the menu
    closeLanguageSubmenuTimeoutId = setTimeout(() => {
      languageSubmenuDisclosure.onClose()
    }, 200) // 200ms delay before closing the menu
  }

  const selectedSummaryParts = availableParts
    .filter((part) => part.generatedPart?.isSelected)
    .map((part) => part.key)

  // need to use separate state, as we invoke 2 mutations, one of which is updateInterview.
  const [isRegenerating, setIsRegenerating] = React.useState(false)

  const makeLanguageMenuClickHandler =
    (summaryLanguage: SummaryLanguage): MenuItemProps['onClick'] =>
    async () => {
      try {
        setIsRegenerating(true)
        customizeDisclosure?.onClose()
        languageSubmenuDisclosure.onClose()

        await updateInterview.mutateAsync({
          summaryLanguage,
        })

        await regenerateSummary.mutateAsync()
      } finally {
        setIsRegenerating(false)
      }
    }

  return (
    <Card className="flex flex-row justify-between items-center p-[1.25rem] flex-wrap rounded-[2.5rem] bg-[#F5F5F5] gap-y-3 mb-5">
      <Flex flexWrap={'wrap'} className={'gap-y-3'}>
        <Button
          mr={3}
          variant={'brand-solid-white'}
          leftIcon={<RefreshIcon />}
          onClick={async () => {
            try {
              setIsRegenerating(true)
              await regenerateSummary.mutateAsync()
            } finally {
              setIsRegenerating(false)
            }
          }}
          isLoading={isRegenerating}
        >
          Regenerate
        </Button>

        <>
          <Menu
            closeOnSelect={false}
            placement={'bottom-start'}
            {...customizeDisclosure}
            onClose={() => {
              customizeDisclosure?.onClose()
              languageSubmenuDisclosure.onClose()
            }}
          >
            <MenuButton
              variant={'brand-solid-white'}
              as={Button}
              isDisabled={isRegenerating}
              leftIcon={<CustomizeIcon />}
              mr={3}
            >
              Customize
            </MenuButton>

            <MenuList>
              {availableParts.map((availablePart) => {
                const isChecked = selectedSummaryParts.includes(
                  availablePart.key,
                )
                const isGenerated = availablePart.isGenerated

                const isGenerating = generatingPartKeys.has(availablePart.key)

                return (
                  <MenuItem
                    alignItems={'center'}
                    as={Flex}
                    w={'100%'}
                    maxW={'300px'}
                    key={availablePart.key}
                    onClick={async () => {
                      if (!isGenerated) {
                        setGeneratingPartKeys((prev) =>
                          new Set(prev).add(availablePart.key),
                        )
                        try {
                          await generateSummaryPart.mutateAsync({
                            partName: availablePart.customSummaryPartPromptId
                              ? undefined
                              : availablePart.name,
                            customSummaryPartPromptId:
                              availablePart.customSummaryPartPromptId,
                          })
                        } finally {
                          setGeneratingPartKeys((prev) => {
                            const next = new Set(prev)
                            next.delete(availablePart.key)
                            return next
                          })
                        }
                      } else if (availablePart.generatedPart) {
                        updateSummaryParts.mutate({
                          summaryParts: [
                            {
                              ...availablePart.generatedPart,
                              isSelected:
                                !availablePart.generatedPart.isSelected,
                            },
                          ],
                        })
                      }
                    }}
                    cursor={'pointer'}
                    isDisabled={isGenerating}
                  >
                    <Checkbox
                      isChecked={isChecked}
                      icon={<SwitchEyeIcon w={5} h={5} />}
                      variant={'ghost'}
                      flexShrink={0}
                      mr={2}
                    />
                    <Text
                      flexGrow={1}
                      minWidth={0}
                      overflow={'hidden'}
                      textOverflow={'ellipsis'}
                      whiteSpace={'nowrap'}
                    >
                      {availablePart.name}
                    </Text>
                    {isGenerating && (
                      <Spinner size="sm" color="brand.500" mx={2} />
                    )}
                    {!isGenerating &&
                      availablePart.customSummaryPartPromptId &&
                      availablePart.generatedPart && (
                        <DeleteSummaryPartButton
                          part={availablePart.generatedPart}
                        />
                      )}
                  </MenuItem>
                )
              })}

              <MenuItem
                onClick={() => {
                  setIsAddCustomSummaryPartShown(true)
                  customizeDisclosure?.onClose()
                  window.scrollTo({
                    top: document.body.scrollHeight,
                    behavior: 'smooth',
                  })
                }}
              >
                <PlusIcon stroke={'black'} /> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Add
                your own topic
              </MenuItem>
              <Divider my={2} mx={'auto'} w={'90%'} borderColor={'gray.300'} />

              <Menu
                isOpen={languageSubmenuDisclosure.isOpen}
                placement="right-end"
              >
                <MenuButton
                  as={MenuItem}
                  icon={<LanguageSvg />}
                  onMouseEnter={handleLanguageSubmenuOpen}
                  onMouseLeave={handleLanguageSubmenuClose}
                  {...(languageSubmenuDisclosure.isOpen && {
                    className: '!bg-[#f6f6f6] hover:bg-[#f6f6f6]',
                  })}
                >
                  <Flex alignItems={'center'} justifyContent={'space-between'}>
                    {interviewType === 'INTAKE'
                      ? 'Role brief language'
                      : 'Scorecard language'}
                    <Box ml={2}>
                      <ArrowRightSvg />
                    </Box>
                  </Flex>
                </MenuButton>
                <Portal>
                  <MenuList
                    onMouseEnter={handleLanguageSubmenuOpen}
                    onMouseLeave={handleLanguageSubmenuClose}
                  >
                    <MenuItem
                      onClick={makeLanguageMenuClickHandler(
                        'SAME_AS_INTERVIEW',
                      )}
                    >
                      <Checkbox
                        pointerEvents={'none'}
                        variant={'ghost'}
                        isChecked={summaryLanguage === 'SAME_AS_INTERVIEW'}
                        icon={<CheckboxIcon />}
                      >
                        Same as the interview
                      </Checkbox>
                    </MenuItem>
                    <MenuItem onClick={makeLanguageMenuClickHandler('ENGLISH')}>
                      <Checkbox
                        pointerEvents={'none'}
                        variant={'ghost'}
                        isChecked={summaryLanguage === 'ENGLISH'}
                        icon={<CheckboxIcon />}
                      >
                        English
                      </Checkbox>
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            </MenuList>
          </Menu>
        </>
      </Flex>

      <Flex gap={3} flexWrap={'wrap'} alignItems={'center'}>
        {interview?.id && isAllowlistedForInterviewPdfExport(user?.email) && (
          <ExportPdfButton interviewId={interview.id} />
        )}

        <Tooltip
          rounded={'1.25rem'}
          py={2}
          px={4}
          bgColor={'blackAlpha.900'}
          label={'Please save scorecard first'}
          isDisabled={!isCopyDisabled}
          shouldWrapChildren={true}
        >
          <CopyContentButton
            isDisabled={isCopyDisabled}
            contentRef={copyRef}
            textCopy="Copy full scorecard"
            textContent={{
              'text/plain': (summaryParts || [])
                .filter((part) => part.isSelected)
                .sort((a, b) => a.order - b.order)
                .map((part) => formatScorecardPartForCopy(part))
                .join('\n\n'),
            }}
          />
        </Tooltip>

        <ViewATSScorecardButton summaryParts={summaryParts} />
      </Flex>
    </Card>
  )
}

const DeleteSummaryPartButton = ({ part }: { part: SummaryPart }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const deleteSummaryPart = useDeleteSummaryPart()

  return (
    <>
      <IconButton
        aria-label={'Delete summary part ' + part.name}
        icon={<DeleteIcon w={3.5} h={3.5} />}
        rounded={'full'}
        minW={7}
        ml={2}
        bg={'transparent'}
        minH={7}
        h={7}
        w={7}
        variant={'brand-solid-white'}
        onClick={(event) => {
          event.stopPropagation()
          onOpen()
        }}
        zIndex={20}
      />
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm deletion</ModalHeader>
          <ModalCloseButton
            onClick={(event) => {
              event.stopPropagation()
              onClose()
            }}
          />
          <ModalBody>
            Confirm deletion of summary part <b>{part.name}</b>?
          </ModalBody>
          <ModalFooter>
            <Button
              rounded={'1.25rem'}
              colorScheme={'red'}
              onClick={async (event) => {
                event.stopPropagation()
                deleteSummaryPart.mutate(part.id)
                onClose()
              }}
            >
              Delete
            </Button>
            <Button
              onClick={(event) => {
                event.stopPropagation()
                onClose()
              }}
              rounded={'1.25rem'}
              variant="brand-solid-white"
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

function SwitchEyeIcon(
  props: { isIndeterminate?: boolean; isChecked?: boolean } & IconProps,
) {
  const { isIndeterminate, isChecked, ...rest } = props

  if (isIndeterminate) {
    // Show ViewIcon with reduced opacity for indeterminate state
    return (
      <Icon
        as={ViewIcon}
        opacity={0.5}
        {...rest}
        style={{
          color: 'gray',
        }}
      />
    )
  }

  return (
    <>
      {isChecked ? (
        <Icon as={ViewIcon} {...rest} />
      ) : (
        <Icon
          as={ViewOffIcon}
          {...rest}
          style={{
            color: 'gray',
          }}
        />
      )}
    </>
  )
}
